import { cn } from './cn.js';
import { cardClasses } from './Card.js';

/** O tom da pílula de situação de cada mês — a mesma palavra do `Badge`. */
export type MonthStripTone = 'sucesso' | 'alerta' | 'neutro';

export interface MonthStripItem {
  key: string;
  /** O nome curto, "Out". */
  label: string;
  /** A situação, "Atual" — a palavra carrega o estado, a cor só reforça. */
  status: string;
  tone: MonthStripTone;
  selected: boolean;
  disabled?: boolean;
  /** O nome por extenso para o leitor de tela, quando o curto não basta. */
  description?: string;
}

// @valor
const TONES: Record<MonthStripTone, string> = {
  sucesso: 'bg-sucesso-suave text-sucesso',
  alerta: 'bg-alerta-suave text-alerta',
  neutro: 'bg-superficie-suave text-texto-suave',
};

/**
 * A faixa de meses — um botão por mês, o escolhido cheio na primária, cada um
 * com a pílula da situação embaixo (28/10/2026, nasceu no Início do Comercial).
 *
 * O componente não sabe o que é um mês: quem chama monta os itens, decide a
 * situação e o que um clique faz. Doze botões não cabem num telefone, e a
 * faixa quebra em quatro e seis colunas em vez de rolar na horizontal.
 *
 * `loading` marca a faixa como ocupada enquanto o mês escolhido carrega —
 * a troca já aconteceu no botão, e o conteúdo abaixo ainda é o do mês velho.
 */
export function MonthStrip({
  items,
  onSelect,
  loading = false,
  label,
}: {
  items: MonthStripItem[];
  onSelect: (key: string) => void;
  loading?: boolean;
  /** O nome do grupo para o leitor de tela, "Mês de recebimento". */
  label: string;
}) {
  return (
    <nav
      aria-label={label}
      aria-busy={loading}
      className={cardClasses({
        padding: false,
        className: 'grid grid-cols-4 gap-2 p-2 sm:grid-cols-6 lg:grid-cols-12',
      })}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          disabled={item.disabled}
          aria-pressed={item.selected}
          aria-label={item.description}
          title={item.description}
          onClick={() => onSelect(item.key)}
          className={cn(
            'flex min-h-11 flex-col items-center justify-center gap-1 rounded-borda px-2 py-2 transition-colors',
            item.selected
              ? 'bg-primaria text-primaria-contraste hover:bg-primaria-forte active:bg-primaria-forte'
              : 'border border-borda bg-superficie text-texto hover:border-borda-forte hover:bg-superficie-suave active:bg-borda disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-borda disabled:hover:bg-superficie',
          )}
        >
          <span className="text-sm font-bold">{item.label}</span>
          <span
            className={cn(
              'rounded-pilula px-2 py-0.5 text-[0.6875rem] font-bold',
              item.selected ? 'bg-primaria-forte' : TONES[item.tone],
            )}
          >
            {item.status}
          </span>
        </button>
      ))}
    </nav>
  );
}
